/**
 * The wine profile as data (docs/13-profil-wein.md): the fields of a wine, their level, the
 * choices offered. The app builds its forms and lists from it; nothing here knows about React.
 *
 * Every field is stored flat in the article's fields, so two devices editing different fields
 * of the same wine do not overwrite each other (mergeEntity: per field, the later input wins).
 * The wine family (F7) is the field `family`: all vintages of a wine share it, and the
 * family-level fields are written to each of them.
 */

import { CORE_REASONS } from "./reasons.ts";
import type { Direction, Reason } from "./reasons.ts";
import { profileReasons } from "./profile.ts";
import type { Profile, ProfileField, ProfileReason } from "./profile.ts";
import { drinkFields, sizeNote, sizeOf } from "./drinks.ts";
import type { ProfileMeasures } from "./measures.ts";

export type { FieldType, ProfileField, ReasonFor } from "./profile.ts";

export const WINE_COLORS = ["Rot", "Weiß", "Rosé", "Orange", "Schaumwein", "Süßwein", "Likörwein"] as const;
/** Bottle sizes in ml (0,187 l Piccolo to 6 l, 0,62 l Clavelin for Vin Jaune); the size is a number of ml since F18. */
export const BOTTLE_SIZES = [187, 200, 250, 375, 500, 620, 700, 750, 1000, 1500, 3000, 5000, 6000] as const;
export const WINE_COUNTRIES = ["Deutschland", "Österreich", "Italien", "Frankreich", "Spanien", "Portugal", "Schweiz", "Griechenland", "Ungarn", "Südafrika", "USA", "Chile", "Argentinien", "Australien", "Neuseeland"] as const;
/** Quality levels as labels say them; suggestions, any other text is fine. */
export const QUALITY_LEVELS = [
  "Deutscher Wein", "Landwein", "Qualitätswein", "Prädikatswein Kabinett", "Prädikatswein Spätlese", "Prädikatswein Auslese", "Prädikatswein Beerenauslese", "Prädikatswein Trockenbeerenauslese", "Prädikatswein Eiswein",
  "VDP.Gutswein", "VDP.Ortswein", "VDP.Erste Lage", "VDP.Große Lage", "Großes Gewächs",
  "Qualitätswein Österreich", "DAC", "AOP", "AOC", "IGP", "Vin de France", "DOCG", "DOC", "IGT", "DOCa", "DO", "Vino de la Tierra", "DOC Portugal",
] as const;

export const GRAPES = [
  "Riesling", "Grauburgunder", "Weißburgunder", "Chardonnay", "Sauvignon Blanc", "Silvaner", "Müller-Thurgau", "Grüner Veltliner", "Gewürztraminer", "Pinot Grigio",
  "Spätburgunder", "Pinot Noir", "Merlot", "Cabernet Sauvignon", "Cabernet Franc", "Syrah", "Sangiovese", "Nebbiolo", "Barbera", "Tempranillo", "Grenache", "Primitivo", "Zweigelt", "Blaufränkisch", "Lemberger", "Dornfelder", "Trollinger",
] as const;

/**
 * Names of the wine's measures (docs/17-getraenke.md, Maße mit Namen): glasses as fractions of a
 * litre, the big bottles by name; Sekt is served in 0,1 l, Likörwein in cl.
 */
export const WINE_MEASURES: ProfileMeasures = {
  glass: { 125: "1/8 l", 250: "1/4 l", 500: "1/2 l" },
  size: { 1500: "Magnum", 3000: "Doppelmagnum" },
  kinds: { Schaumwein: { glass: { 100: "0,1 l" } }, Likörwein: { glass: { 50: "5 cl" } } },
};

export const WINE_FIELDS: readonly ProfileField[] = drinkFields(
  {
    producer: "Erzeuger",
    producerHint: "Weingut, Kellerei oder Handelshaus",
    name: "Name",
    nameHint: "Cuvée, Lage oder Linie, z. B. „Merlot Riserva“",
    kind: "Farbe",
    kindHint: "Schaumwein auch für Perlwein (Secco, Frizzante)",
    kinds: WINE_COLORS,
    size: "Flaschengröße",
    sizes: BOTTLE_SIZES,
    containers: ["Flasche", "Bag-in-Box", "Dose"],
    salePrice: "Verkaufspreis je Flasche",
    glassHint: "wenn offen ausgeschenkt, z. B. „1/8 6,50; 1/4 12,50“, „0,1 7,50“ oder „5 cl 6,50“",
    listNoHint: "Nummer auf der Weinkarte; das Personal sucht danach",
    countries: WINE_COUNTRIES,
  },
  {
    identity: [{ key: "vintage", label: "Jahrgang", type: "year", level: "article", required: true, filter: true, group: "main", hint: "leer lassen für „ohne Jahrgang“" }],
    origin: [
      { key: "appellation", label: "Appellation oder Lage", type: "text", level: "family", group: "origin" },
      { key: "grapes", label: "Rebsorten", type: "multi", level: "family", filter: true, options: GRAPES, group: "origin", hint: "durch Komma getrennt, nach Anteil" },
      { key: "quality", label: "Qualitätsstufe", type: "text", level: "article", options: QUALITY_LEVELS, group: "origin", hint: "z. B. „Prädikatswein Kabinett“, „DOCG“, „AOP“; steht auf der Karte" },
    ],
    moreFirst: [{ key: "taste", label: "Geschmack", type: "choice", level: "family", options: ["trocken", "halbtrocken", "feinherb", "lieblich", "süß", "brut nature", "extra brut", "brut", "extra dry", "dry", "demi-sec", "doux"], group: "more" }],
    more: [
      { key: "closure", label: "Verschluss", type: "choice", level: "article", options: ["Naturkork", "Schraubverschluss", "Glas", "Kronkorken", "Kunststoff"], group: "more" },
      { key: "drink", label: "Trinkfenster", type: "range", level: "article", group: "more", hint: "von–bis, Jahre" },
      { key: "rating", label: "Bewertung", type: "text", level: "article", group: "more", hint: "z. B. „93 Falstaff“" },
      // Per article, not per family: often only an old vintage past its window goes to the kitchen (docs/13-profil-wein.md).
      { key: "cooking", label: "Zum Kochen", type: "yesno", level: "article", filter: true, group: "more", hint: "für Rezepte vorschlagen, etwa ein einfacher Wein oder einer über dem Trinkfenster; nicht auf der Karte" },
      // Analysis and label (docs/13-profil-wein.md, Analyse und Etikett): per vintage, so per article.
      { key: "sugar", label: "Restzucker", type: "number", level: "article", unit: "g/l", group: "label", hint: "aus der Analyse; ohne Geschmack schlägt die App ihn danach vor" },
      { key: "acidity", label: "Säure", type: "number", level: "article", unit: "g/l", group: "label", hint: "Gesamtsäure" },
      { key: "ph", label: "pH-Wert", type: "number", level: "article", group: "label" },
      { key: "energy", label: "Brennwert", type: "number", level: "article", unit: "kcal/100 ml", group: "label", hint: "Nährwertangabe des E-Labels" },
      { key: "ingredients", label: "Zutaten", type: "longtext", level: "article", group: "label", hint: "Zutatenliste des E-Labels, z. B. „Trauben, Konservierungsstoff: Sulfite“" },
      { key: "bottler", label: "Abfüller", type: "text", level: "family", group: "label", hint: "mit Anschrift, wenn nicht der Erzeuger" },
      { key: "eLabel", label: "E-Label (Link)", type: "text", level: "article", group: "label", hint: "Adresse hinter dem QR-Code auf dem Rückenetikett" },
    ],
  },
);

export const FIELD_GROUPS: Record<string, string> = { main: "Wein", origin: "Herkunft", gastro: "Gastronomie", label: "Analyse und Etikett", more: "Mehr" };

/**
 * The taste of a still wine by residual sugar and total acidity, as the EU defines the words
 * (Regulation (EU) 2019/33, Annex III): trocken up to 4 g/l, or 9 g/l when the acidity is at most
 * 2 g/l below the sugar; halbtrocken up to 12, or 18 with acidity at most 10 below; lieblich up to
 * 45; süß above. Null without sugar. Sparkling wine has its own words (brut …) and is not derived.
 */
export function tasteOf(sugar: unknown, acidity?: unknown): string | null {
  if (typeof sugar !== "number" || !Number.isFinite(sugar) || sugar < 0) return null;
  const acid = typeof acidity === "number" && Number.isFinite(acidity) ? acidity : null;
  if (sugar <= 4 || (sugar <= 9 && acid !== null && acid >= sugar - 2)) return "trocken";
  if (sugar <= 12 || (sugar <= 18 && acid !== null && acid >= sugar - 10)) return "halbtrocken";
  if (sugar <= 45) return "lieblich";
  return "süß";
}

/** Packaging units the profile suggests (docs/13-profil-wein.md, Voreinstellungen); factor in bottles. */
export const WINE_PACKS = [
  { name: "Karton à 3", plural: "Kartons à 3", factor: 3, containers: ["Flasche"] },
  { name: "Karton à 6", plural: "Kartons à 6", factor: 6, containers: ["Flasche"] },
  { name: "Karton à 12", plural: "Kartons à 12", factor: 12, containers: ["Flasche"] },
] as const;

/** How names are written. `vintages: false` (F23): the workspace keeps no vintages, a wine without one is not marked „o. J.“. */
export interface NameOptions {
  vintages?: boolean;
}

/** The master data record `card/settings` holds the workspace's settings, among them `vintages` (F23). */
export const SETTINGS_ID = "settings";

/** Whether the workspace keeps vintages (F23): yes unless its settings say no. */
export const vintagesTracked = (settings: Record<string, unknown> | undefined): boolean => settings?.vintages !== false;

/** The name a wine is shown and searched by: "Erzeuger Name Jahrgang", the size when not 0,75 l. */
export function wineName(f: Record<string, unknown>, options: NameOptions = {}): string {
  const parts = [f.producer, f.wine].filter((v) => typeof v === "string" && v.trim() !== "").map((v) => (v as string).trim());
  if (typeof f.vintage === "number") parts.push(String(f.vintage));
  else if (options.vintages !== false) parts.push("o. J.");
  const note = sizeNote(sizeOf(f));
  if (note) parts.push(note);
  return parts.join(" ");
}

/** Whether an article was made with the wine profile (the demo wines have only a name). */
export const isProfiled = (f: Record<string, unknown>) => typeof f.producer === "string" && typeof f.wine === "string";

/**
 * The reasons of the wine profile (docs/13-profil-wein.md, Gründe). Ids of the core are kept where
 * the meaning is the same, so reports over profiles stay comparable; the profile only renames.
 * The first of each direction is the default: "Lieferung" in, "Verkauf" out (privately "Getrunken").
 */
export const WINE_REASONS: readonly ProfileReason[] = [
  { id: "delivery", label: "Lieferung", class: "procurement", for: "both" },
  { id: "purchase", label: "Einkauf", class: "procurement", for: "both" },
  { id: "gift-received", label: "Geschenk erhalten", class: "freeReceipt", for: "private" },
  { id: "opening", label: "Startbestand", class: "opening", for: "both" },
  { id: "drunk", label: "Getrunken", class: "private", for: "private" },
  { id: "sale", label: "Verkauf (Flasche)", class: "sale", for: "business" },
  { id: "pour", label: "Ausschank (Flasche geöffnet)", class: "sale", for: "business" },
  { id: "kitchen", label: "Küche", class: "consumption", for: "business" },
  { id: "private", label: "Privatentnahme", class: "private", for: "business" },
  { id: "staff", label: "Personal", class: "staff", for: "business" },
  { id: "tasting", label: "Verkostung", class: "freeIssue", for: "both" },
  { id: "on-the-house", label: "Aufs Haus", class: "freeIssue", for: "business" },
  { id: "gift", label: "Verschenkt", class: "freeIssue", for: "private" },
  { id: "breakage", label: "Bruch", class: "knownLoss", for: "both" },
  { id: "corked", label: "Korkfehler", class: "knownLoss", for: "both" },
  { id: "return-supplier", label: "Reklamation an Lieferant", class: "returnToSupplier", for: "both" },
];

/** The reasons offered for a direction, for a business or a private cellar, default first. */
export function wineReasons(direction: Direction, kind: "business" | "private" = "business"): ProfileReason[] {
  return profileReasons(WINE_PROFILE, direction, kind);
}


/** The wine profile as one record (docs/13-profil-wein.md): what the app KellerLog is made of. */
export const WINE_PROFILE: Profile = {
  id: "wein",
  app: "KellerLog",
  vocabulary: {
    article: "Wein",
    articles: "Weine",
    unit: "Flasche",
    units: "Flaschen",
    unitShort: "Fl.",
    take: { business: "Entnehmen", private: "Trinken" },
    workspace: { business: { one: "Betrieb", the: "den Betrieb", of: "des Betriebs" }, private: { one: "Keller", the: "den Keller", of: "des Kellers" } },
    search: "Wein suchen",
  },
  fields: WINE_FIELDS,
  groups: FIELD_GROUPS,
  nameFields: ["producer", "wine", "vintage"],
  nameWithout: "o. J.",
  categoryField: "color",
  packs: WINE_PACKS,
  measures: WINE_MEASURES,
  usualSize: 750,
  defaults: { allergens: ["sulphites"] },
  reasons: WINE_REASONS,
  tracking: "lot",
  countMode: "article",
  starter: [
    { id: "keller", name: "Weinkeller", locations: [["regal-a", "Regal A"], ["regal-b", "Regal B"], ["regal-c", "Regal C"]] },
    { id: "bar", name: "Bar", locations: [["kuehlschrank", "Kühlschrank"], ["tagesregal", "Tagesregal"]] },
  ],
};
